import { useEffect, useSyncExternalStore } from "react";
import { customerPrintingStateV1Schema } from "@lifescapsule/contracts";
import { publicApiRequest } from "./api";

/**
 * Whether customers can order printed copies right now, and the one way every page says so while
 * they can't (redesign, 2026-11-02). The owner switches printing on by hand; every page reads the
 * service's answer while it runs (`GET /printing`), so opening printing needs no new build, and an
 * emergency stop closes it everywhere at once (Stage 2, A7).
 *
 * Until the service has answered, or while it can't be asked, the state is CHECKING: a page then
 * says neither that printing is open nor that it isn't, and asks again soon. Every decision treats
 * anything but OPEN as closed.
 *
 * The words are W3 print review's: nothing is promised that no email or page delivers.
 */
export type PrintingState = "OPEN" | "CLOSED" | "CHECKING";

/** How long one answer is trusted before a page asks again. */
const FRESH_MILLISECONDS = 60_000;
/** How soon a check that failed is tried again. */
const RETRY_MILLISECONDS = 10_000;
/** How long the last answer still stands while the service can't be asked. */
const STANDS_MILLISECONDS = 5 * 60_000;

let known: { state: "OPEN" | "CLOSED"; at: number } | null = null;
let asking: Promise<boolean> | null = null;
let failedAt = 0;
let retry: ReturnType<typeof setTimeout> | null = null;
/** Tests only: an answer set by a test stands until the test clears it. */
let pinned = false;
const listeners = new Set<() => void>();
const tell = () => listeners.forEach((listener) => listener());

function fresh() {
  if (pinned) return true;
  const now = Date.now();
  if (known && now - known.at < FRESH_MILLISECONDS) return true;
  // After a failure the next question waits a little, never a whole minute.
  return failedAt > 0 && now - failedAt < RETRY_MILLISECONDS;
}

/** What is known now: the last answer while it stands, otherwise still checking. */
export function printingState(): PrintingState {
  if (known && (pinned || Date.now() - known.at < STANDS_MILLISECONDS)) return known.state;
  return "CHECKING";
}

/**
 * Asks the service once (one question at a time). A failure keeps the last answer while it
 * stands, or leaves the state CHECKING, and asks again in a few seconds; it resolves closed.
 */
export function checkPrintingOpen(): Promise<boolean> {
  if (fresh()) return Promise.resolve(printingState() === "OPEN");
  asking ??= publicApiRequest<{ data?: unknown }>("/printing", { cache: "no-store" })
    .then((response) => customerPrintingStateV1Schema.parse(response.data).printing)
    .then(
      (answer) => {
        const before = printingState();
        known = { state: answer, at: Date.now() };
        failedAt = 0;
        asking = null;
        if (before !== answer) tell();
        return answer === "OPEN";
      },
      () => {
        const before = printingState();
        failedAt = Date.now();
        asking = null;
        if (retry === null)
          retry = setTimeout(() => {
            retry = null;
            if (listeners.size) void checkPrintingOpen();
          }, RETRY_MILLISECONDS);
        if (before !== printingState()) tell();
        return false;
      },
    );
  return asking;
}

/**
 * Whether printing is open, for code outside a component: closed unless the service has said
 * OPEN. It starts a check when the answer is missing or old; `usePrintingState` re-renders on it.
 */
export function printingOpen(): boolean {
  if (!fresh()) void checkPrintingOpen();
  return printingState() === "OPEN";
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Open, closed, or still checking, kept current while the page is shown. */
export function usePrintingState(): PrintingState {
  const state = useSyncExternalStore(subscribe, printingState, () => "CHECKING" as const);
  useEffect(() => {
    void checkPrintingOpen();
    const again = () => {
      if (!document.hidden) void checkPrintingOpen();
    };
    document.addEventListener("visibilitychange", again);
    const timer = window.setInterval(again, FRESH_MILLISECONDS);
    return () => {
      document.removeEventListener("visibilitychange", again);
      window.clearInterval(timer);
    };
  }, []);
  return state;
}

/** Whether printing is open, for a decision: anything but a confirmed OPEN is closed. */
export function usePrintingOpen(): boolean {
  return usePrintingState() === "OPEN";
}

/** Tests only: the service's answer, standing until cleared (null: nothing known yet). */
export function setPrintingOpenForTests(open: boolean | null) {
  known = open === null ? null : { state: open ? "OPEN" : "CLOSED", at: Date.now() };
  pinned = open !== null;
  asking = null;
  failedAt = 0;
  if (retry !== null) clearTimeout(retry);
  retry = null;
  tell();
}

/** Printing isn't open: said where there's nothing else to do yet. */
export const PRINTING_NOT_OPEN = "Ordering printed copies isn’t open yet.";

/** Printing isn't open, for a finished or approved book: it is saved and nothing waits on the buyer. */
export const PRINTING_NOT_OPEN_NOTHING_TO_DO =
  "Ordering printed copies isn’t open yet, so there’s nothing more to do for now.";

/** Printing isn't open, before a book exists: the included copy is ordered from the finished book. */
export const PRINTING_NOT_OPEN_ORDER_LATER =
  "Ordering printed copies isn’t open yet: you’ll order your included copy from your finished book once it opens.";

/** A finished book whose voucher includes its printed copy (the guided finish's words). */
export const INCLUDED_HARDCOVER = "Your voucher includes one printed hardcover copy of this book.";

/**
 * A finished or approved book whose voucher includes its printed copy, while ordering isn't open
 * (book quality X3, 2026-11-02). "Isn't open yet" alone, right after the voucher promised a
 * hardcover, left a gift-giver wondering whether it was still coming. What is true now: the copy
 * is the book's own (the server grants it to the book when the voucher is used, and keeps it there),
 * it is ordered from this same finished page once ordering opens, and nothing waits on the buyer.
 * No date and no email are promised: neither exists.
 */
export const INCLUDED_HARDCOVER_KEPT = `Your voucher’s printed hardcover is kept for this book. ${PRINTING_NOT_OPEN} Once it opens, you’ll order it here, and until then there’s nothing more to do.`;

/** The same, once printing is open and it can be ordered. */
export const INCLUDED_HARDCOVER_WITH_SHIPPING =
  "Your voucher includes one printed hardcover copy of this book, with standard US shipping.";

/**
 * A printed copy that is ordered: where its progress is followed (book quality X5, 2026-11-02).
 * Pages said "We’ll email you as it’s printed and when it ships", but the order emails are switched
 * off (`GELATO_FULFILLMENT_NOTIFICATIONS_ENABLED` is false live), so no such email goes. The order's
 * own page shows each step and the tracking, and is reached again from My Library.
 */
export const FOLLOW_ORDER_HERE = "You can follow its progress here.";

/**
 * The same, on a book's own page once its copy is ordered, beside the link to the order: that page
 * shows no progress itself, only the order's page does (X5 review).
 */
export const FOLLOW_ORDER_ON_ITS_PAGE = "Follow its progress on its order page.";

/** The same, on the order page while it waits for the printer: closing it loses nothing. */
export const FOLLOW_ORDER_HERE_AND_COME_BACK = `${FOLLOW_ORDER_HERE} You can close this page and come back to it from My Library.`;

/** The same, once the printer has it: tracking follows on this page. */
export const FOLLOW_ORDER_HERE_UNTIL_SHIPPED = `${FOLLOW_ORDER_HERE} Tracking appears here as soon as your book ships.`;

/** A book whose voucher has no printed copy (W3 print review's words). */
export const NO_PRINTED_COPY = "This book has no confirmed included printed copy.";

/** The way to an approved book's print files once printing is open: the step before ordering. */
export const REVIEW_PRINT_FILES = "Review print files";

/**
 * The same way in while printing isn't open (or is still being checked): the files are there to
 * look at, and nothing is ordered from them, so it never reads as a print step (C4, 2026-11-02).
 */
export const SEE_PRINT_FILES = "See your print files";

/** What the way to a book's print files says, by whether printing is open. */
export function printFilesLabel(state: PrintingState) {
  return state === "OPEN" ? REVIEW_PRINT_FILES : SEE_PRINT_FILES;
}
